import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getTenants, patchTenant, postTenant, removeTenant } from "store/admin/tenantWideData";
import { AppDispatch, AppState } from "store/configureStore";
import { Button, Form, Table } from "react-bootstrap";
import { useConfirmDialog } from "pages/UI/ConfirmDialogComponent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserEdit } from "@fortawesome/free-solid-svg-icons";
import TablePagination from "./TablePagination";
import CustomerFormModal, { CustomerFormData } from "./CustomerFormModal";
import TenantUsersModal from "./TenantUserModal";

export interface Tenant {
  Id: number;
  Guid: string;
  Name: string;
  Address: string;
  Phone: string;
  RegistrationNo: string;
}

const SettingsCustomerPage = () => {
  const { t } = useTranslation("parts");
  const dispatch = useDispatch<AppDispatch>();
  const { isLoggedIn } = useSelector((state: AppState) => state.users.auth);
  const { list: tenants, loading: tenantsLoading } = useSelector(
    (state: AppState) => state.admin.tenantWideData.tenants
  );

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(getTenants());
    }
  }, [isLoggedIn, dispatch]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [isTenantModalOpen, setIsTenantModalOpen] = useState(false);
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [tenantsUpdated, setTenantsUpdated] = useState(false);
  const [manageSaUsers, setManageSaUsers] = useState(true);

  //--- Pagination Logic
  const currentTenants = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return tenants.slice(startIndex, startIndex + itemsPerPage);
  }, [tenants, currentPage, itemsPerPage]);

  useEffect(() => {
    if (tenantsUpdated && !tenantsLoading) {
      dispatch(getTenants());
      setTenantsUpdated(false);
    }
  }, [tenantsUpdated, tenantsLoading, dispatch]);

  const handleUsersModal = useCallback((tenant: Tenant) => {
    setManageSaUsers(false);
    setSelectedTenant(tenant);
    setIsUsersModalOpen(true);
  }, []);

  const handleSAUsersModal = useCallback(() => {
    setManageSaUsers(true);
    const saTenant = tenants.find(t => t.Id === 1);
    if (saTenant) {
      setSelectedTenant(saTenant);
      setIsUsersModalOpen(true);
    }
  }, [tenants]);

  const handleEditTenant = useCallback((tenant: Tenant) => {
    setSelectedTenant(tenant);
    setIsTenantModalOpen(true);
  }, []);

  const handleShowAddModal = () => {
    setSelectedTenant(null);
    setIsTenantModalOpen(true);
  };

  const handleRemove = useCallback(async (tenant: Tenant): Promise<boolean> => {
    const isConfirmed = await askForConfirmation();
    if (!isConfirmed) return false;
    try {
      if (tenant.Id) {
        dispatch(removeTenant(tenant.Id));
      }
      if (currentTenants.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
      setTenantsUpdated(true);
      return true;
    } catch (err) {
      console.error(err);
    }
    return false;
  }, [currentTenants, currentPage, dispatch]);

  const askForConfirmation = (
    title: string = t("common:confirmationTitle"),
    message: string = t("parts:agreements.deleteConfirmationModalTitle")
  ): Promise<boolean> => confirm(title, message);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(event.target.value, 10);
    if (value > 0) {
      setItemsPerPage(value);
      setCurrentPage(1);
    }
  };

  const handleSaveCustomer = (customerData: CustomerFormData, connectionString: string) => {
    if (selectedTenant) {
      dispatch(
        patchTenant({
          ...customerData,
          Guid: selectedTenant.Guid,
          Id: selectedTenant.Id
        })
      );
    } else {
      const newCustomer: Tenant = {
        ...customerData,
        Id: 0 // will be set by backend
      };
      dispatch(postTenant(newCustomer, connectionString));
    }
    setTenantsUpdated(true);
    setIsTenantModalOpen(false);
  };

  return (
    <div className="settings-card card">
      <h2>{t("pages:settings.customers")}</h2>

      <div className="add-new-container">
        <Button variant="primary" onClick={handleShowAddModal}>
          + {t("settings.addNewCustomer")}
        </Button>
        <Button variant="primary" className="ms-3" onClick={handleSAUsersModal}>
          {t("settings.manageSAUsers")}
        </Button>
      </div>

      <div className="tables-controls">
        <Form.Group controlId="itemsPerPage" className="show-entries-group">
          <Form.Label>{t("common:table.rows")} </Form.Label>
          <Form.Control
            type="number"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          />
        </Form.Group>
      </div>

      { (tenantsUpdated || tenantsLoading) ? 
        <div>{t("common:processing")}</div>
      :
        <Table striped borderless hover responsive className="customer-table">
        <thead>
          <tr>
            <th>{t("approvalWorkflows.values.Name")}</th>
            <th>{t("settings.tableHeaders.registrationNo")}</th>
            <th>{t("approvalWorkflows.values.Phone")}</th>
            <th>{t("approvalWorkflows.values.Address")}</th>
            <th className="text-center"></th>
          </tr>
        </thead>
        <tbody>
          {currentTenants.map((tenant, index) => (
            <tr key={tenant.Id || tenant.Guid || `fallback-${index}`}>
              <td>{tenant.Name}</td>
              <td>{tenant.RegistrationNo}</td>
              <td>{tenant.Phone}</td>
              <td>{tenant.Address}</td>
              <td className="text-center">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleUsersModal(tenant)}
                  title="Show Users"
                >
                  <FontAwesomeIcon icon={faUserEdit} />
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleEditTenant(tenant)}
                  title="Edit Customer Details"
                >
                  {t("common:form.buttons.edit")}
                </Button>
                {<Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleRemove(tenant)}
                  disabled={tenant.Id === 1} // Disable delete for SA tenant
                >
                  {t("absences.buttons.remove")}
                </Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      }

      <TablePagination
        currentPage={currentPage}
        totalItems={tenants.length}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
      />

      <CustomerFormModal
        show={isTenantModalOpen}
        onHide={() => {setIsTenantModalOpen(false); setSelectedTenant(null);}}
        onFormSubmit={handleSaveCustomer}
        initialData={selectedTenant}
      />

      <TenantUsersModal
        show={isUsersModalOpen}
        onHide={() => {setIsUsersModalOpen(false); setSelectedTenant(null);}}
        tenant={selectedTenant}
        manageSAUsers={manageSaUsers}
      />

      <ConfirmDialogComponent />
    </div>
  );
};

export default SettingsCustomerPage;
